import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogPortal,
  DialogOverlay,
} from "../../components/ui/Dialog";
import { Badge } from "../../components/ui/Badge";
import crossIcon from "../../assets/images/cross.svg";
import { InterviewCase } from ".";
import { getTopicColor } from "src/utils/topicColors";
import { useEffect } from "react";

interface CasePromptDialogProps {
  isOpen: boolean;
  onClose: () => void;
  caseData: InterviewCase | null;
}

export default function CasePromptDialog({
  isOpen,
  onClose,
  caseData,
}: CasePromptDialogProps) {
  // Scroll to top when dialog opens
  useEffect(() => {
    if (isOpen) {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [isOpen]);

  if (!caseData) return null;
  console.log("caseData", caseData);
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogPortal>
        <DialogOverlay className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm overflow-y-auto" />
        <DialogContent className="fixed left-[50%] top-[10%] z-50 grid w-full max-w-3xl translate-x-[-50%] gap-6 border bg-background p-8 shadow-2xl duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-2xl my-8">
          <div className="max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <div className="flex items-center justify-between">
                <DialogTitle className="text-xl font-bold">
                  {caseData.title}
                </DialogTitle>
              </div>
              <DialogDescription className="sr-only">
                Interview case study information
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-6 py-4">
              {/* Info Grid */}
              <div className="grid grid-cols-2 gap-x-12 gap-y-4">
                <div>
                  <h3 className="text-sm text-gray-500">Industry</h3>
                  <p className="font-medium">{caseData.industry}</p>
                </div>
                <div>
                  <h3 className="text-sm text-gray-500">Company</h3>
                  <p className="font-medium">{caseData.company}</p>
                </div>
                <div>
                  <h3 className="text-sm text-gray-500">
                    Estimated Time to Solve
                  </h3>
                  <p className="font-medium">{caseData.expectedTime}</p>
                </div>
                <div>
                  <h3 className="text-sm text-gray-500">Style</h3>
                  <p className="font-medium">
                    {caseData.isInterviewerLed
                      ? "Interviewer-led"
                      : "Candidate-led"}
                  </p>
                </div>
              </div>

              {/* Topics */}
              <div>
                <h3 className="text-sm text-gray-500 mb-2">Topics</h3>
                <div className="flex flex-wrap gap-2">
                  {caseData.topics.map((topic, index) => (
                    <Badge
                      key={index}
                      variant="secondary"
                      className={`px-3 py-1 text-sm font-medium rounded-full ${getTopicColor(
                        topic.name
                      )}`}
                    >
                      {topic.name}
                    </Badge>
                  ))}
                </div>
              </div>

              {/* Case Prompt */}
              <div>
                <h3 className="text-sm text-gray-500 mb-2">Case Prompt</h3>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-gray-700">{caseData.caseProblem}</p>
                </div>
              </div>
            </div>
          </div>
        </DialogContent>
      </DialogPortal>
    </Dialog>
  );
}
